import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Search, Check } from "lucide-react";
import { api } from "@/lib/api";

const YEARS = (() => {
  const now = new Date().getFullYear() + 1;
  const arr = [];
  for (let y = now; y >= 1990; y--) arr.push(String(y));
  return arr;
})();

export function SearchSelect({ label, value, options, onChange, placeholder = "اختر", testid, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const selected = norm.find((o) => o.value === value);
  const filtered = q ? norm.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : norm;

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref} className="relative">
      {label && <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">{label}</label>}
      <button type="button" data-testid={testid} disabled={disabled} onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-sm outline-none ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${selected ? "text-white" : "text-[var(--text-subtle)]"}`}>
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDown size={16} className="text-[var(--text-muted)] shrink-0" />
      </button>
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-[var(--border-c)] bg-[var(--bg-card)] shadow-xl overflow-hidden">
          <div className="flex items-center gap-2 border-b border-[var(--border-c)] px-3 py-2">
            <Search size={15} className="text-[var(--text-muted)]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid={testid ? `${testid}-search` : undefined}
              placeholder="ابحث..." className="w-full bg-transparent text-sm text-white outline-none" />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-xs text-[var(--text-muted)]">لا توجد نتائج</p>
            ) : filtered.map((o) => (
              <button type="button" key={o.value} data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                onClick={() => { onChange(o.value); setOpen(false); setQ(""); }}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm text-right hover:bg-[var(--bg-secondary)] ${o.value === value ? "text-[var(--amber)]" : "text-white"}`}>
                <span className="truncate">{o.label}</span>
                {o.value === value && <Check size={14} className="text-[var(--amber)]" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function VehiclePicker({ value, onChange, includeYear = true, includeTrim = true }) {
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [trims, setTrims] = useState([]);

  useEffect(() => { api.get("/vehicles/makes").then(({ data }) => setMakes(data)); }, []);

  useEffect(() => {
    if (!value.make) { setModels([]); return; }
    api.get("/vehicles/models", { params: { make: value.make } }).then(({ data }) => setModels(data));
  }, [value.make]);

  useEffect(() => {
    if (!includeTrim || !value.make || !value.model) { setTrims([]); return; }
    api.get("/vehicles/trims", { params: { make: value.make, model: value.model } }).then(({ data }) => setTrims(data));
  }, [value.make, value.model, includeTrim]);

  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <SearchSelect label="الشركة المصنعة" testid="vp-make" value={value.make || ""}
        options={makes.map((m) => ({ value: m.name, label: m.name_en ? `${m.name} — ${m.name_en}` : m.name }))}
        onChange={(v) => set({ make: v, model: "", trim: "" })} placeholder="اختر الشركة" />
      <SearchSelect label="الموديل" testid="vp-model" value={value.model || ""}
        options={models.map((m) => m.name)} disabled={!value.make}
        onChange={(v) => set({ model: v, trim: "" })} placeholder={value.make ? "اختر الموديل" : "اختر الشركة أولًا"} />
      {includeYear && (
        <SearchSelect label="سنة الصنع" testid="vp-year" value={value.year ? String(value.year) : ""}
          options={YEARS} onChange={(v) => set({ year: v })} placeholder="اختر السنة" />
      )}
      {includeTrim && (
        <div>
          <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">الفئة / المحرك (اختياري)</label>
          {trims.length > 0 ? (
            <SearchSelect testid="vp-trim" value={value.trim || ""} options={trims.map((t) => t.name)}
              onChange={(v) => set({ trim: v })} placeholder="اختر الفئة" />
          ) : (
            <input data-testid="vp-trim-input" value={value.trim || ""} onChange={(e) => set({ trim: e.target.value })}
              placeholder="مثال: 2.5L أو V6" className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-sm text-white outline-none" />
          )}
        </div>
      )}
    </div>
  );
}
